import React from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Bar } from 'recharts';
import { CalculationResult } from '../../types';
import { colorSystem } from '../../utils/colorSystem';
import { formatAxisValue, formatTooltipValue } from '../../utils/formatters';
import { formatNumberWithCommas } from '../../utils/formatters';

interface RealizedBenefitOverTimeChartProps {
  results: CalculationResult[];
}

// Utility function to truncate Y-axis labels
const truncateLabel = (label: string, maxLength: number = 16): string => {
  if (label.length <= maxLength) return label;
  return label.substring(0, maxLength - 3) + '...';
};

export function RealizedBenefitOverTimeChart({ results }: RealizedBenefitOverTimeChartProps) {

  const chartData = React.useMemo(() => {
    colorSystem.reset();
    
    // Sort results by date to ensure correct baseline calculation
    const sortedResults = [...results].sort((a, b) => new Date(a.calculationDate).getTime() - new Date(b.calculationDate).getTime());

    if (sortedResults.length === 0) {
      return { data: [], roleKeys: [] };
    }

    // Identify the baseline impact hours from the first data point
    const baselineLinearImpactHours = sortedResults[0].annualLinearImpactHours || 0;
    const baselineExponentialImpactHours = sortedResults[0].annualExponentialImpactHours || 0;

    const dataMap = new Map<string, { [key: string]: any }>();
    
    sortedResults.forEach(result => {
      const date = result.calculationDate;
      if (!dataMap.has(date)) {
        dataMap.set(date, {
          date,
          ept: result.ept,
          // Calculate realized benefits relative to the baselines
          realizedLinearBenefit: baselineLinearImpactHours - (result.annualLinearImpactHours || 0),
          realizedExponentialBenefit: baselineExponentialImpactHours - (result.annualExponentialImpactHours || 0),
          values: {}
        });
      }
    });

    const data = Array.from(dataMap.entries())
      .map(([date, values]) => ({
        date,
        ...values
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Role keys are not directly used for lines in this chart, but kept for consistency if needed later
    const roleKeys = Array.from(new Set(
      results.map(r => `${r.role}-${r.experience}`)
    ));

    return { data, roleKeys };
  }, [results]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium text-gray-900 mb-2">
          {new Date(label).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </p>
        {payload.map((entry: any) => {
          if (entry.dataKey === 'ept') {
            return (
              <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-700">EPT:</span>
                <span className="font-medium">{formatTooltipValue(entry.value, '', 's')}</span>
              </div>
            );
          }

          const value = entry.value;
          const isNegative = value < 0;
          const displayValue = Math.abs(value);

          const benefitType = entry.dataKey === 'realizedLinearBenefit' ? 'Linear Benefit' : 'Exponential Benefit';

          return (
            <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-700">{benefitType}:</span>
              <span 
                className="font-medium"
                style={{ color: isNegative ? '#EF4444' : '#10B981' }}
              >
                {isNegative ? '-' : '+'}{formatNumberWithCommas(displayValue)} hours/year
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full h-[600px] bg-white p-6 rounded-xl shadow-sm chart-container">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Realized Benefit Over Time (Linear vs. Exponential)</h3>
          <p className="text-sm text-gray-500">
            Linear and exponential productivity gains/losses relative to baseline EPT
          </p>
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height="87%">
        <ComposedChart 
          data={chartData.data}
          margin={{ top: 60, right: 30, left: 20, bottom: 60 }}
        >
          <defs>
            <linearGradient id="eptGradient" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#A855F7" stopOpacity={0.3}/>
              <stop offset="100%" stopColor="#7C3AED" stopOpacity={0.8}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            label={{ 
              value: 'Analysis Date', 
              position: 'bottom', 
              offset: 40,
              style: { fontWeight: 'bold' }
            }}
            tick={{ dy: 10 }}
          />
          <YAxis
            yAxisId="left"
            width={100}
            label={{ 
              value: truncateLabel('Realized Benefit (Hours/Year)'), 
              angle: 0,
              position: 'top',
              dy: -30,
              textAnchor: 'start',
              dx: 50,
              style: { fontWeight: 'bold' }
            }}
            tickFormatter={formatAxisValue}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            width={100}
            domain={[0, 6]} // EPT typically ranges from 0 to 6 seconds
            label={{
              value: truncateLabel('EPT (seconds)'),
              angle: 0,
              position: 'top',
              dy: -30,
              textAnchor: 'end',
              dx: -50,
              style: { fontWeight: 'bold' }
            }}
            tickFormatter={formatAxisValue}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            verticalAlign="bottom" 
            height={36}
            wrapperStyle={{
              paddingTop: '20px',
              marginBottom: '-45px'
            }}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="realizedLinearBenefit"
            name="Linear Benefit"
            stroke="#3B82F6"
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
            connectNulls={true}
          />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="realizedExponentialBenefit"
            name="Exponential Benefit"
            stroke="#10B981"
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
            connectNulls={true}
          />
          <Bar
            yAxisId="right"
            dataKey="ept"
            name="EPT"
            fill="url(#eptGradient)"
            opacity={0.6}
            barSize={20}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}